import { useState, useEffect } from 'react';
import { Plus, PieChart, BarChart3, Search, FolderTree } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { StatCard } from '../../components/ui/StatCard';
import { Tabs } from '../../components/ui/Tabs';
import { Table } from '../../components/ui/Table';
import { AnalyticAccountFormModal } from '../../components/analytic-accounting/AnalyticAccountFormModal';
import { AnalyticReportView } from '../../components/analytic-accounting/AnalyticReportView';
import { useAnalyticAccounts } from '../../hooks/useAnalyticAccounts';
import { useAnalyticReport } from '../../hooks/useAnalyticReport';
import { useFiscalYearStore } from '../../stores/fiscalYearStore';
import { usePermissions } from '../../hooks/usePermissions';
import toast from 'react-hot-toast';
import type { AnalyticAccount } from '../../types/analytic-accounting';
import {
  ANALYTIC_ACCOUNT_TYPE_LABELS,
  ANALYTIC_ACCOUNT_TYPE_COLORS,
} from '../../types/analytic-accounting';

export function AnalyticAccountingPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<AnalyticAccount | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const { currentFiscalYear } = useFiscalYearStore();
  const { can } = usePermissions();
  const canManage = can('manage_analytic_accounts');

  const {
    accounts,
    activeAccounts,
    loading,
    error,
    loadAccounts,
    createAccount,
    updateAccount,
    deleteAccount,
  } = useAnalyticAccounts();

  const {
    reportData,
    loading: reportLoading,
    loadReport,
  } = useAnalyticReport();

  useEffect(() => {
    loadAccounts({ type: typeFilter || undefined, search: searchTerm || undefined });
  }, [loadAccounts, typeFilter, searchTerm]);

  useEffect(() => {
    if (currentFiscalYear?.id) {
      loadReport({ fiscalYearId: currentFiscalYear.id });
    }
  }, [loadReport, currentFiscalYear?.id]);

  const handleCreate = async (data: any) => {
    const result = await createAccount(data);
    if (result) {
      toast.success('Compte analytique créé');
      setShowCreateModal(false);
    }
  };

  const handleUpdate = async (data: any) => {
    if (!selectedAccount) return;
    const result = await updateAccount(selectedAccount.id, data);
    if (result) {
      toast.success('Compte analytique mis à jour');
      setShowEditModal(false);
      setSelectedAccount(null);
    }
  };

  const handleDelete = async (account: AnalyticAccount) => {
    if (!canManage) return;
    const success = await deleteAccount(account.id);
    if (success) {
      toast.success('Compte analytique supprimé');
    }
  };

  const handleEdit = (account: AnalyticAccount) => {
    setSelectedAccount(account);
    setShowEditModal(true);
  };

  // Stats
  const costCenters = activeAccounts.filter(a => a.type === 'cost_center').length;
  const projects = activeAccounts.filter(a => a.type === 'project').length;
  const departments = activeAccounts.filter(a => a.type === 'department').length;

  const columns = [
    {
      header: 'Code',
      accessor: (account: AnalyticAccount) => (
        <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
          {account.code}
        </span>
      ),
    },
    {
      header: 'Nom',
      accessor: (account: AnalyticAccount) => (
        <div>
          <p className="font-medium text-foreground">{account.name}</p>
          {account.description && (
            <p className="text-xs text-muted-foreground truncate max-w-[200px]">{account.description}</p>
          )}
        </div>
      ),
    },
    {
      header: 'Type',
      accessor: (account: AnalyticAccount) => {
        const colors = ANALYTIC_ACCOUNT_TYPE_COLORS[account.type];
        return (
          <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}>
            {ANALYTIC_ACCOUNT_TYPE_LABELS[account.type]}
          </span>
        );
      },
    },
    {
      header: 'Statut',
      accessor: (account: AnalyticAccount) => (
        <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
          account.isActive
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
        }`}>
          {account.isActive ? 'Actif' : 'Inactif'}
        </span>
      ),
    },
    {
      header: 'Actions',
      accessor: (account: AnalyticAccount) => (
        <div className="flex items-center gap-1">
          {canManage && (
            <>
              <Button variant="ghost" size="sm" onClick={() => handleEdit(account)}>
                Modifier
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(account)} className="text-destructive">
                Supprimer
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-muted border-t-primary"></div>
      </div>
    );
  }

  const accountsTab = (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Rechercher un compte analytique..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input pl-10 w-full"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="form-select"
        >
          <option value="">Tous les types</option>
          <option value="cost_center">Centre de coût</option>
          <option value="project">Projet</option>
          <option value="department">Département</option>
          <option value="other">Autre</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      <Card noPadding>
        <Table
          columns={columns}
          data={accounts}
          loading={loading}
          emptyTitle="Aucun compte analytique"
          emptyMessage="Créez votre premier compte analytique pour ventiler vos écritures"
        />
      </Card>
    </div>
  );

  const reportTab = (
    <AnalyticReportView
      data={reportData}
      loading={reportLoading}
      onRefresh={() => currentFiscalYear?.id ? loadReport({ fiscalYearId: currentFiscalYear.id }) : undefined}
    />
  );

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Comptabilité Analytique</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Ventilation multi-axes et analyse de rentabilité
          </p>
        </div>
        {canManage && (
          <Button
            variant="success"
            icon={Plus}
            onClick={() => setShowCreateModal(true)}
          >
            Nouveau compte
          </Button>
        )}
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard title="Comptes actifs" value={activeAccounts.length} icon={FolderTree} />
        <StatCard title="Centres de coût" value={costCenters} icon={PieChart} />
        <StatCard title="Projets" value={projects} icon={BarChart3} />
        <StatCard title="Départements" value={departments} icon={FolderTree} />
      </div>

      {/* Onglets */}
      <Card noPadding>
        <Tabs
          tabs={[
            { id: 'accounts', label: 'Plan analytique', content: accountsTab },
            { id: 'report', label: 'Rapport analytique', content: reportTab },
          ]}
          defaultTab="accounts"
        />
      </Card>

      {/* Modales */}
      {showCreateModal && (
        <AnalyticAccountFormModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreate}
          existingAccounts={accounts}
        />
      )}

      {showEditModal && selectedAccount && (
        <AnalyticAccountFormModal
          isOpen={showEditModal}
          onClose={() => { setShowEditModal(false); setSelectedAccount(null); }}
          onSubmit={handleUpdate}
          account={selectedAccount}
          existingAccounts={accounts}
        />
      )}
    </div>
  );
}
